import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, Search, X } from 'lucide-react';
import { PositionStage, type Page } from '@clawhire/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { useDebouncedValue } from '@/lib/use-debounce';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EnumBadge } from '@/components/data/EnumBadge';
import { unwrapList } from './api';
import type { PositionListItem, PositionRef } from './types';

export interface PositionPickerProps {
  value: PositionRef | null;
  onChange: (position: PositionRef | null) => void;
  /** Only open positions (`GET /positions?openOnly=true`). */
  openOnly?: boolean;
  /** Shown first, e.g. the positions this candidate is already proposed for. */
  suggested?: PositionRef[];
  suggestedLabel?: string;
  /** Positions that cannot be chosen (already proposed). */
  excludeIds?: readonly string[];
  disabled?: boolean;
  id?: string;
  className?: string;
}

/**
 * Search-as-you-type position selector (`GET /positions?text=`). Renders the results as a
 * plain list under the search box; the chosen position is shown as a row with a "Change" button.
 */
export function PositionPicker({
  value,
  onChange,
  openOnly = false,
  suggested = [],
  suggestedLabel = 'Suggested',
  excludeIds = [],
  disabled = false,
  id,
  className,
}: PositionPickerProps) {
  const [text, setText] = React.useState('');
  const debounced = useDebouncedValue(text.trim(), 250);
  const listId = React.useId();

  const query = useQuery({
    queryKey: queryKeys.list('positions', { openOnly, text: debounced, pageSize: 20 }),
    queryFn: () =>
      api.get<Page<PositionListItem>>('/positions', {
        query: { openOnly: openOnly || undefined, text: debounced || undefined, pageSize: 20 },
      }),
    enabled: !value && !disabled,
    staleTime: 15_000,
  });

  const excluded = new Set(excludeIds);
  const suggestedVisible = suggested.filter((p) => !excluded.has(p.id));
  const suggestedIds = new Set(suggestedVisible.map((p) => p.id));
  const results = unwrapList<PositionListItem>(query.data).filter(
    (p) => !excluded.has(p.id) && !(debounced === '' && suggestedIds.has(p.id)),
  );

  if (value) {
    return (
      <div className={cn('flex items-center gap-2 rounded-md border p-2', className)}>
        <Briefcase className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <div className="min-w-0 flex-1 text-sm">
          <span className="font-medium tabular-nums">{value.requestNo}</span> <span>{value.role}</span>
          {value.stage ? (
            <EnumBadge def={PositionStage} value={value.stage} size="sm" className="ml-2" />
          ) : null}
        </div>
        {disabled ? null : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(null)}
            aria-label="Change position"
          >
            <X aria-hidden="true" /> Change
          </Button>
        )}
      </div>
    );
  }

  const renderItem = (p: PositionRef & { stage?: string; intermediary?: { name: string } | null }) => (
    <li key={p.id}>
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none"
        onClick={() => onChange(p)}
        disabled={disabled}
      >
        <Briefcase className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">
          <span className="font-medium tabular-nums">{p.requestNo}</span> {p.role}
          {p.intermediary?.name ? (
            <span className="text-muted-foreground"> · {p.intermediary.name}</span>
          ) : null}
        </span>
        {p.stage ? <EnumBadge def={PositionStage} value={p.stage} size="sm" /> : null}
      </button>
    </li>
  );

  return (
    <div className={cn('space-y-2', className)}>
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          id={id}
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Search by request number or role…"
          aria-label="Search positions"
          aria-controls={listId}
          className="pl-8"
          disabled={disabled}
          autoComplete="off"
        />
      </div>
      <div id={listId} className="max-h-64 overflow-y-auto rounded-md border p-1">
        {suggestedVisible.length > 0 && debounced === '' ? (
          <>
            <p className="px-2 py-1 text-xs font-medium text-muted-foreground">{suggestedLabel}</p>
            <ul aria-label={suggestedLabel}>{suggestedVisible.map((p) => renderItem(p))}</ul>
          </>
        ) : null}
        {query.isFetching ? <p className="px-2 py-1.5 text-xs text-muted-foreground">Searching…</p> : null}
        {query.isError ? (
          <p className="px-2 py-1.5 text-xs text-destructive">
            {query.error instanceof Error ? query.error.message : 'Could not load positions'}
          </p>
        ) : null}
        {results.length > 0 ? (
          <>
            {suggestedVisible.length > 0 && debounced === '' ? (
              <p className="px-2 py-1 text-xs font-medium text-muted-foreground">
                {openOnly ? 'Open positions' : 'Positions'}
              </p>
            ) : null}
            <ul aria-label={openOnly ? 'Open positions' : 'Positions'}>
              {results.map((p) => renderItem(p))}
            </ul>
          </>
        ) : !query.isFetching && query.isSuccess && suggestedVisible.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-muted-foreground">
            {debounced
              ? `No ${openOnly ? 'open ' : ''}position matches "${debounced}".`
              : `No ${openOnly ? 'open ' : ''}positions.`}
          </p>
        ) : null}
      </div>
    </div>
  );
}
